import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, LifeBuoy, CheckCircle2, MessageSquare } from "lucide-react";

export default function TicketsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ object: "", request: "" });
  const [reply, setReply] = useState({ id: null, note: "" });

  const load = async () => setItems((await api.get("/tickets")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.object || !form.request) { toast.error("Oggetto e descrizione obbligatori"); return; }
    try {
      await api.post("/tickets", form);
      toast.success("Ticket aperto");
      setForm({ object: "", request: "" }); setOpen(false); load();
    } catch (e) { toast.error("Errore"); }
  };

  const close = async (id) => {
    await api.patch(`/tickets/${id}`, { status: "closed", note: "" });
    toast.success("Chiuso"); load();
  };

  const sendReply = async () => {
    if (!reply.note) return;
    try {
      await api.patch(`/tickets/${reply.id}`, { status: "open", note: reply.note });
      toast.success("Risposta inviata");
      setReply({ id: null, note: "" }); load();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="p-4 md:p-12" data-testid="tickets-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Supporto interno</div>
          <h1 className="font-display text-4xl">Ticket</h1>
          <p className="text-[#555] mt-1 text-sm">Sistema di richieste interne · risposte · chiusura</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-ticket-btn" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] h-11 btn-glow">
              <Plus className="w-4 h-4 mr-2"/> Nuovo ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl border-0 max-w-md">
            <DialogHeader><DialogTitle className="font-display text-2xl">Apri ticket</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Oggetto *</Label><Input data-testid="ticket-object" className="rounded-xl mt-1" value={form.object} onChange={(e)=>setForm({...form, object: e.target.value})}/></div>
              <div><Label>Descrizione *</Label><Textarea data-testid="ticket-request" className="rounded-xl mt-1 min-h-[140px]" value={form.request} onChange={(e)=>setForm({...form, request: e.target.value})} placeholder="Spiega il problema o la richiesta…"/></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="ticket-save" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00]">Invia</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {items.length === 0 && <div className="col-span-2 card-smart p-12 text-center text-[#757575]"><LifeBuoy className="w-10 h-10 mx-auto mb-3 text-[#FF7A29]/50"/>Nessun ticket aperto.</div>}
        {items.map((t) => (
          <div key={t.id} className="card-smart p-6" data-testid={`ticket-${t.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${t.status === "open" ? "bg-[#FF7A29] animate-pulse" : "bg-gray-400"}`}/>
                <span className={`text-[10px] uppercase tracking-widest font-semibold ${t.status === "open" ? "text-[#FF7A29]" : "text-gray-400"}`}>
                  {t.status === "open" ? "Aperto" : "Chiuso"}
                </span>
              </div>
              <span className="text-xs text-[#757575]">{new Date(t.created_at).toLocaleDateString("it-IT")}</span>
            </div>
            <h3 className="font-display text-xl mb-2">{t.object}</h3>
            <div className="text-xs text-[#757575] mb-3">da {t.author_name}</div>
            <p className="text-sm text-[#333] leading-relaxed mb-4 whitespace-pre-wrap">{t.request}</p>

            {t.replies?.length > 0 && (
              <div className="border-t border-[#EDE5D8] pt-3 mb-3 space-y-2">
                {t.replies.map((r) => (
                  <div key={r.id} className="bg-[#FFF0E3]/60 rounded-xl p-3">
                    <div className="text-[10px] font-semibold text-[#E85D00]">{r.author_name} · {new Date(r.at).toLocaleString("it-IT")}</div>
                    <div className="text-sm mt-1">{r.note}</div>
                  </div>
                ))}
              </div>
            )}

            {reply.id === t.id ? (
              <div className="border-t border-[#EDE5D8] pt-3 space-y-2">
                <Textarea className="rounded-xl" value={reply.note} onChange={(e)=>setReply({...reply, note: e.target.value})} placeholder="Scrivi una risposta…"/>
                <div className="flex gap-2">
                  <Button size="sm" onClick={sendReply} className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] h-8">Invia</Button>
                  <Button size="sm" variant="ghost" onClick={()=>setReply({id: null, note: ""})} className="rounded-full h-8">Annulla</Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 pt-3 border-t border-[#EDE5D8]">
                <Button size="sm" variant="outline" onClick={()=>setReply({id: t.id, note: ""})} className="rounded-full h-8 text-xs border-[#EDE5D8]"><MessageSquare className="w-3 h-3 mr-1"/>Rispondi</Button>
                {t.status === "open" && <Button size="sm" onClick={()=>close(t.id)} className="rounded-full h-8 text-xs bg-[#0A0A0A] hover:bg-[#FF7A29]"><CheckCircle2 className="w-3 h-3 mr-1"/>Chiudi</Button>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
